/* eslint-disable react-hooks/exhaustive-deps */
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Lock as LockIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HyperPayService } from '../../../../../../services/hyperPayService';
import { Section } from '../../../../../../shared/components/common/section';
import { ButtonWrapper } from '../../../../../../shared/components/formUtilities/useButton';
import { InputWrapper } from '../../../../../../shared/components/formUtilities/useInput';
import { Toaster } from '../../../../../../shared/components/common/toaster';
import { FormikProvider, useFormik } from 'formik';
import {LanguageService} from '../../../../../../services/languageService';
import { CURRENCY } from '../../../../../../config/appConfig';
import { currencyFormatterHelper } from '../../../../../../helpers/currencyFormatterHelper';
import visaLogo from '../../../../../../assets/images/Visa_Inc._logo.png';
import mastercardLogo from '../../../../../../assets/images/Mastercard-logo.png';
import madaLogo from '../../../../../../assets/images/mada.png';
import pciDss from '../../../../../../assets/images/pci-dss.png';


export const Checkout = () => {
	const { t } = useTranslation();
	const hyperPayService = new HyperPayService();
	const languageService = new LanguageService();

	const [loading, setLoading] = useState(false);
	const [checkoutData, setCheckoutData] = useState(null);
	const [scriptLoaded, setScriptLoaded] = useState(false);
	const [error, setError] = useState(null);

	
	const language = languageService.getCurrentLanguage();


	const form = useFormik({
		initialValues: { amount: '' },
		onSubmit: () => {
			initiateCheckout();
		},
		enableReinitialize: true,
		validateOnChange: false,
		validateOnBlur: false,
		validateOnMount: false,
		validate: () => {
			// Return empty object to prevent any validation errors
			return {};
		},
	});

const loadHyperPayWidget = (scriptUrl, integrity) => {
	if (!scriptUrl) {
		console.error('HyperPay scriptUrl is missing from checkout response');
		setError(t('invalidServerResponse'));
		setLoading(false);
		return;
	}
	console.log('Loading HyperPay widget with scriptUrl:', scriptUrl);
	console.log('Integrity:', integrity);

	const amountValue = form.values.amount?.toString().replace(/,/g, '') || '';
	const amount = amountValue ? parseFloat(amountValue) : null;
	const confirmDepositText = t('confirmDeposit');
	const formattedAmount = amount ? currencyFormatterHelper(amount) : null;
	const currencySymbol = CURRENCY;
	
	const optionsScript = document.createElement('script');
	optionsScript.innerHTML = `
		var wpwlOptions = {
			locale: "${language}",
			style: "logos",
			paymentTarget: "_top",
			brandDetection: true,
			brandDetectionType: "binlist",
			imageStyle: "svg",
			onReady: function() {
				console.log('HyperPay widget ready');
					// Style the Pay now button to match deposit button color and center it
				var checkButton = function() {
					var payButton = document.querySelector('.wpwl-button, .wpwl-button-pay, button[type="submit"]');
					if (payButton) {
						var buttonText = payButton.textContent || payButton.innerText || '';
						if (buttonText && (buttonText.includes('Pay') || buttonText.includes('Confirm') || buttonText.includes('Submit'))) {
							var amountValue = ${amount ? amount.toFixed(2) : 'null'};
							var formattedAmount = ${formattedAmount ? `'${formattedAmount}'` : 'null'};
							var currencySymbol = '${currencySymbol}';
							if (amountValue && formattedAmount) {
								payButton.textContent = '${confirmDepositText} – ' + currencySymbol + ' ' + formattedAmount;
							} else {
								payButton.textContent = '${confirmDepositText}';
							}
						}
						
						payButton.style.backgroundColor = '#33cd87';
						payButton.style.color = '#ffffff';
						payButton.style.border = 'none';
							payButton.style.margin = '0 auto'; // Center the button
							payButton.style.display = 'block'; // Make it block to center
							payButton.style.width = '100%'
						payButton.addEventListener('mouseenter', function() {
							this.style.backgroundColor = '#33cd87';
							this.style.opacity = '0.9';
						});
						payButton.addEventListener('mouseleave', function() {
							this.style.backgroundColor = '#33cd87';
							this.style.opacity = '1';
						});
					} else {
						setTimeout(checkButton, 200);
					}
				};
				setTimeout(checkButton, 500);
				
				// Center the form wrapper
				var formWrapper = document.querySelector('.paymentWidgets, .wpwl-wrapper');
				if (formWrapper) {
					formWrapper.style.display = 'flex';
					formWrapper.style.flexDirection = 'column';
					formWrapper.style.alignItems = 'center';
				}
			},
			onError: function(error) {
				console.error('HyperPay widget error:', error);
			}
		};
	`;
	document.head.appendChild(optionsScript);

	// Load HyperPay widget script
	const widgetScript = document.createElement('script');
	widgetScript.src = scriptUrl;
	
		// Add integrity if provided (for PCI DSS v4.0 compliance)
	if (integrity) {
		widgetScript.integrity = integrity;
		widgetScript.crossOrigin = 'anonymous';
	}
	
	widgetScript.async = true;
	widgetScript.onload = () => {
		console.log('HyperPay widget script loaded successfully');
		setScriptLoaded(true);
		setLoading(false);
	};
	widgetScript.onerror = (error) => {
		console.error('Failed to load HyperPay widget script:', error);
			// Try loading without integrity as fallback
		if (integrity) {
			console.log('Retrying without integrity...');
			widgetScript.removeAttribute('integrity');
			widgetScript.removeAttribute('crossorigin');
		} else {
			setError(t('failedToLoadWidget'));
			setLoading(false);
		}
	};

	document.body.appendChild(widgetScript);
};

	const initiateCheckout = async () => {
		// Remove commas from amount if present (currency formatting)
		const amountValue = form.values.amount?.toString().replace(/,/g, '') || '';
		const amount = parseFloat(amountValue);
		
		if (!amount || amount <= 0 || isNaN(amount)) {
			setError(t('invalidAmount'));
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const response = await hyperPayService.createCheckout({
				language,
				amount,
			});

			if (response?.data) {
				setCheckoutData(response.data);
				loadHyperPayWidget(response.data.scriptUrl, response.data.integrity);
			} else {
				throw new Error(t('invalidServerResponse'));
			}
		} catch (err) {
			console.error('Checkout error:', err);
			setError(
				err?.response?.data?.message ||
					t('checkoutFailed')
			);
			setLoading(false);
		}
	};

	return (
		<Box className="container mx-auto px-4 py-8">
			<Section>
				<Stack spacing={3}>
					<Typography variant="h4" className="text-center">
						{t('paymentCheckout')}
					</Typography>

					{error && (
						<Toaster
							state={true}
							message={error}
							severity="error"
							onClose={() => setError(null)}
						/>
					)}

					{/* Amount Input Form */}
					{!checkoutData && (
						<FormikProvider value={form}>
							<Box className="flex flex-col gap-4 items-center">
								<Box 
									className="flex flex-row items-start gap-4"
									sx={{
										'& .amount-input-container': {
											display: 'flex',
											flexDirection: 'row',
											alignItems: 'flex-start',
											gap: '16px',
										},
										'& .amount-label': {
											minWidth: '120px',
											paddingTop: '8px',
											fontSize: '14px',
											color: '#343434',
											textAlign: 'left',
										},
										'& .amount-input-wrapper': {
											width: '300px',
											maxWidth: '100%',
											'& .MuiTextField-root': {
												width: '100%',
												'& .MuiOutlinedInput-root': {
													borderRadius: '4px',
													'& fieldset': {
														borderColor: '#e0e0e0',
														borderWidth: '1px',
													},
													'&:hover fieldset': {
														borderColor: '#bdbdbd',
													},
													'&.Mui-focused fieldset': {
														borderColor: '#1976d2',
														borderWidth: '1px',
													},
													'& .MuiInputBase-input': {
														padding: '8.5px 14px',
														fontSize: '14px',
														height: 'auto',
													},
												},
											},
										},
									}}
								>
									<Box className="amount-input-container">
										<Typography className="amount-label">
										{t('amount')}
										</Typography>
										<Box className="amount-input-wrapper">
											<InputWrapper
												inputClass="currency-symbol"
												isCurrencyText
												label=""
												name="amount"
												autoFocus
												hideErrorMessage={true}
												placeholder={t('enterAmount') || 'Enter Amount'}
											/>	
										</Box>
									</Box>
								</Box>

								<Box className="flex justify-center">
									<ButtonWrapper
										onClick={() => form.submitForm()}
										loading={loading}
										disabled={(() => {
											const amountValue = form.values.amount?.toString().replace(/,/g, '') || '';
											const amount = parseFloat(amountValue);
											return !amountValue || isNaN(amount) || amount <= 0;
										})()}
									label={t('continue')}
										classList="px-8 py-3"
									/>
								</Box>
							</Box>
						</FormikProvider>
					)}

					{/* Payment Widget Container */}
					{checkoutData && (
						<Box 
							className="mt-6"
							sx={{
								'& .paymentWidgets, & .wpwl-wrapper': {
									display: 'flex !important',
									flexDirection: 'column !important',
									alignItems: 'center !important',
								},
								'& .wpwl-button': {
									backgroundColor: '#33cd87 !important',
									color: '#ffffff !important',
									border: 'none !important',
									margin: '0 auto !important',
									display: 'block !important',
									'&:hover': {
										backgroundColor: '#33cd87 !important',
										opacity: '0.9',
									},
									'&:active': {
										backgroundColor: '#33cd87 !important',
									},
									'&:focus': {
										backgroundColor: '#33cd87 !important',
									},
								},
								'& .wpwl-button-pay': {
									backgroundColor: '#33cd87 !important',
									color: '#ffffff !important',
									border: 'none !important',
									margin: '0 auto !important',
									display: 'block !important',
									'&:hover': {
										backgroundColor: '#33cd87 !important',
										opacity: '0.9',
									},
									'&:active': {
										backgroundColor: '#33cd87 !important',
									},
									'&:focus': {
										backgroundColor: '#33cd87 !important',
									},
								},
							}}
							>
							<form
								action={checkoutData.shopperResultUrl}
								className="paymentWidgets"
								data-brands="VISA MASTER MADA"
							/>

							{!scriptLoaded && (
								<Box className="flex justify-center py-8">
									<CircularProgress />
									<Typography className="ml-4">
									{t('loadingPaymentForm')}
									</Typography>
								</Box>
							)}

							<Box sx={{ width: '100%', mb: '2%', mt: '-1%' }}>
								<Typography 
									variant="body2" 
									className="text-center text-gray-500"
									sx={{ fontSize: '14px' }}
								>
									{t('paymentAuthorizationText')}
								</Typography>
								<Box 
									sx={{ 
										width: '48%', 
										height: '2px', 
										backgroundColor: '#e0e0e0',
										margin: '0 auto'
									}} 
								/>
							</Box>
							{/* Footer Section */}
							{scriptLoaded && (
								<Box 
									className="mt-6 w-full max-w-md"
									sx={{ display: 'contents' }}
								>							
									<Box className="flex flex-col gap-3 items-center">
										<Box className="flex items-start gap-2 text-gray-500 mr-8">
											<LockIcon sx={{ fontSize: '14px',  mt: '2px' }} />
											<Typography variant="body2" sx={{ fontSize: '14px' }}>
												{t('securePaymentText')} <Box component="span" sx={{ fontWeight: 'bold' }}>{t('poweredByHyperPay')}</Box>
											</Typography>
										</Box>
										
										<Box className="flex items-start gap-2 text-gray-500">
											<CheckCircleIcon sx={{ fontSize: '14px', mt: '2px' }} />
											<Box className="flex flex-col">
												<Typography variant="body2" sx={{ fontSize: '14px' }}>
													{t('cardDetailsEncrypted')}
												</Typography>
												<Typography variant="body2" sx={{ fontSize: '14px' }}>
													{t('cardDetailsNotStored')}
												</Typography>
											</Box>
										</Box>
										
										{/* Payment Method Logos */}
										<Box className="flex items-center gap-4 mt-2">
											<img 
												src={madaLogo} 
												alt="mada" 
												style={{ height: '20px', width: 'auto', objectFit: 'contain' }}
											/>
											<img 
												src={visaLogo} 
												alt="Visa" 
												style={{ height: '20px', width: 'auto', objectFit: 'contain' }}
											/>
											<img 
												src={mastercardLogo} 
												alt="Mastercard" 
												style={{ height: '20px', width: 'auto', objectFit: 'contain' }}
											/>
											<img 
												src={pciDss} 
												alt="PCI Dss" 
												style={{ height: '20px', width: 'auto', objectFit: 'contain' }}
											/>
										</Box>
									</Box>
								</Box>
							)}
						</Box>
					)}
				</Stack>
			</Section>
		</Box>
	);
};

